import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { secureStorage } from '../lib/secureStorage';
import { markFreshAuthentication } from '../lib/security';
import { AuthContextType, AuthUser, Clinic, SignInOutcome, User } from '../types';
import { emailChallengeOf } from '../lib/api/modules/emailVerification';
import { safeConsole } from '../lib/piiProtection';
import { broadcastAuthEvent, onAuthEvent } from '../lib/authChannel';
import { useIdleLogout } from '../hooks/useIdleLogout';
import { clearPdfCache } from '../lib/pdfCache';
import { useAccountLanguageSync } from '../i18n/useAccountLanguageSync';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Proactively refresh the session on a fixed cadence, comfortably under the 24h access-token TTL, so
// a still-active user is never bounced to /login. We no longer decode the JWT for its expiry — the
// token isn't in JS any more (it lives only in the httpOnly cookie); a periodic cookie-based refresh
// is enough and far simpler. authModule.refresh() rotates the cookie server-side.
const REFRESH_INTERVAL_MS = 20 * 60 * 1000;

/** How long logout waits for the server to revoke the session before reloading anyway. */
const LOGOUT_API_TIMEOUT_MS = 3000;

/** Leaves the app for the login page with a full reload. */
function reloadToLogin() {
  window.location.replace('/login');
}

/** Browser-stored data that belongs to the signed-in user (unsaved lab results are patient data). */
const USER_LOCAL_KEY_PREFIXES = ['lab-entry-draft-'];

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [clinic, setClinic] = useState<Clinic | null>(null);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /** Starts (or restarts) the periodic silent session refresh. Idempotent. */
  const startTokenRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    refreshTimerRef.current = setInterval(() => {
      // Fire-and-forget: a failed refresh surfaces as a 401 on the next real request, which
      // fetchApi already handles (silent retry, then redirect-to-login).
      api.auth.refresh().catch(() => {});
    }, REFRESH_INTERVAL_MS);
  }, []);

  const stopTokenRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, []);

  /**
   * Drops everything the app holds for the signed-in account: every cached server answer (most query keys
   * carry no user or clinic id, so a cache kept across accounts would show the previous account's data) and
   * the user's browser-stored drafts. UI preferences (theme, language, sidebar, tour) stay.
   */
  const resetClientData = useCallback(() => {
    queryClient.cancelQueries().catch(() => {});
    queryClient.clear();
    clearPdfCache(); // documents opened in this tab belong to the account that opened them
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && USER_LOCAL_KEY_PREFIXES.some((p) => key.startsWith(p))) localStorage.removeItem(key);
      }
    } catch {
      // storage blocked: nothing stored to remove
    }
  }, [queryClient]);

  /** The account this tab currently shows (for cross-tab logins: was it a different account?). */
  const currentUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    currentUserIdRef.current = user?.id ?? null;
  }, [user?.id]);

  /**
   * Establishes React auth state from the shared, httpOnly `auth_token` cookie by asking the server
   * who it is (`GET /auth/me`) — the browser sends the cookie automatically. This is the single source
   * of truth for "am I logged in", so a second tab/window inherits an existing login instead of
   * showing /login, and a cross-tab `login` event re-runs it. Returns whether a session was found.
   */
  const bootstrapFromCookie = useCallback(async (): Promise<boolean> => {
    try {
      const response = await api.auth.getCurrentUser();

      // Backend returns {data: {user, clinic}} which fetchApi wraps in {data: ...}.
      // On 401 (no/expired cookie) fetchApi already tried one silent refresh, then returns
      // { error }, so response.data is undefined and we fall through to the logged-out state.
      const responseData = response.data?.data || response.data;

      if (responseData && responseData.user) {
        // Another tab signed in as a different account: nothing of this tab's account may remain.
        if (currentUserIdRef.current && currentUserIdRef.current !== responseData.user.id) resetClientData();
        setUser({ id: responseData.user.id, email: responseData.user.name });
        setUserData(responseData.user);
        setClinic(responseData.clinic);
        secureStorage.setAuthToken(); // mark this tab's session (no token is stored)
        startTokenRefresh();
        return true;
      }
      setUser(null);
      setUserData(null);
      setClinic(null);
      secureStorage.removeAuthToken();
      return false;
    } catch {
      safeConsole.error('Auth initialization error');
      setUser(null);
      setUserData(null);
      setClinic(null);
      secureStorage.removeAuthToken();
      return false;
    }
  }, [startTokenRefresh, resetClientData]);

  /** Clears this tab's auth state and per-tab caches WITHOUT calling the logout API. Shared by the
   *  local signOut and by a `logout` broadcast from another tab (which already hit the API). */
  const clearLocalAuthState = useCallback(() => {
    stopTokenRefresh();
    resetClientData();
    sessionStorage.removeItem('lastActivity');
    localStorage.removeItem('lastUsedPharmacyId');
    localStorage.removeItem('favoritePharmacies');
    localStorage.removeItem('favoriteDiagnoses');
    sessionStorage.removeItem('recentPatients');
    localStorage.removeItem('recentPatients');
    setUser(null);
    setUserData(null);
    setClinic(null);
    secureStorage.removeAuthToken();
  }, [stopTokenRefresh, resetClientData]);

  useEffect(() => {
    // Skip the staff "am I logged in" check entirely when the app is opened directly on a patient
    // route — a patient-only session has no staff auth_token cookie, so this call was guaranteed to
    // 401 (harmless, but pure noise on every single patient-portal page load, forever, since
    // AuthProvider wraps PatientAuthProvider unconditionally and only bootstraps once per app mount
    // regardless of route). A dual-role user who starts on a patient URL and later navigates
    // in-app to a staff route without a page reload was never covered by this single mount-time
    // bootstrap anyway — today's behavior already doesn't re-run it on route change, so this changes
    // nothing for that case; a real page load on a staff route still bootstraps normally.
    // Patient-portal routes only: '/patient' and '/patient/…'. A bare prefix test also matched the STAFF
    // routes '/patients', '/patients/register' and '/patient-details', so a page refresh there skipped the
    // bootstrap, left `user` null and bounced every staff role to /login.
    const path = window.location.pathname;
    if (path === '/patient' || path.startsWith('/patient/')) {
      setLoading(false);
      return;
    }
    bootstrapFromCookie().finally(() => setLoading(false));
  }, [bootstrapFromCookie]);

  // Cross-tab sync: converge every tab of this browser on login/logout instead of waiting for the
  // next navigation. On a remote logout, clear locally (route guards then redirect to /login); on a
  // remote login, re-bootstrap from the now-present cookie.
  useEffect(() => {
    return onAuthEvent((event) => {
      if (event === 'logout') {
        clearLocalAuthState();
      } else if (event === 'login') {
        bootstrapFromCookie();
      }
    });
  }, [bootstrapFromCookie, clearLocalAuthState]);

  /** Shared by signIn and completeMfaLogin — both land here once a real session is established. */
  const applyLoginResponse = (response: { data?: any; error?: string }) => {
    const responseData = response.data?.data || response.data;
    const outerData = response.data as any;

    if (!responseData || !responseData.user) {
      safeConsole.error('Response data is missing user');
      throw new Error('Invalid response from server: missing user data');
    }

    // A new session starts from nothing, even if the previous one ended without a clean logout.
    resetClientData();

    // Reset session activity on login to prevent immediate logout
    sessionStorage.setItem('lastActivity', String(Date.now()));

    setUser({ id: responseData.user.id, email: responseData.user.name });
    setUserData(responseData.user);
    setClinic(responseData.clinic);

    // Multi-branch: store the default active clinic from login response.
    // TenantContext.tsx picks this up on mount.
    if (outerData?.activeClinicId) {
      secureStorage.setActiveClinicId(outerData.activeClinicId);
    }

    // The session now lives in the httpOnly cookie the login response set; mark this tab and start
    // the periodic silent refresh.
    secureStorage.setAuthToken();
    // A real sign-in (not the session restore a refresh does): the screen lock may start unlocked.
    markFreshAuthentication();
    startTokenRefresh();

    // Tell other tabs of this browser to pick up the new session from the cookie.
    broadcastAuthEvent('login');
  };

  const signIn = async (email: string, password: string) => {
    try {
      const response = await api.auth.login(email, password);

      if (response.error) {
        throw new Error(response.error);
      }

      return signInWithResponse(response);
    } catch (error) {
      safeConsole.error('Sign-in error');
      throw error;
    }
  };

  /**
   * Turns a login-shaped response into an outcome. No session yet when the account has MFA on
   * (completeMfaLogin finishes it) or must first prove its email (completeEmailVerificationLogin,
   * medopd-docs 96); otherwise the session is applied.
   */
  const signInWithResponse = (response: { data?: unknown; error?: string }): SignInOutcome => {
    if (response.error) throw new Error(response.error);
    const emailChallenge = emailChallengeOf(response.data);
    if (emailChallenge) {
      return { mfaRequired: false as const, emailVerificationRequired: true as const, emailChallenge };
    }
    type MaybeMfa = { mfaRequired?: boolean; mfaPendingToken?: string };
    const outer = response.data as (MaybeMfa & { data?: MaybeMfa }) | undefined;
    const responseData = outer?.data ?? outer;
    if (responseData?.mfaRequired) {
      return { mfaRequired: true as const, mfaPendingToken: responseData.mfaPendingToken as string };
    }
    applyLoginResponse(response);
    return { mfaRequired: false as const };
  };

  const completeEmailVerificationLogin = async (pendingToken: string, challengeId: string, code: string) => {
    const response = await api.emailVerification.completeLogin(pendingToken, challengeId, code);
    if (response.error) {
      const err = new Error(response.error) as Error & { code?: string };
      err.code = response.errorCode;
      throw err;
    }
    return signInWithResponse(response);
  };

  const completeMfaLogin = async (mfaPendingToken: string, code?: string, backupCode?: string) => {
    try {
      const response = await api.mfa.verify(mfaPendingToken, code, backupCode);
      if (response.error) {
        throw new Error(response.error);
      }
      applyLoginResponse(response);
    } catch (error) {
      safeConsole.error('MFA verification error');
      throw error;
    }
  };

  const signOut = useCallback(async () => {
    try {
      // Clear this tab's state, every cached server answer and per-user drafts (DPDP Act 2023 § 8: no
      // retention beyond session), and stop the refresh timer.
      clearLocalAuthState();

      // Tell every other tab of this browser to drop to the login screen too.
      broadcastAuthEvent('logout');

      // Revoke the session server-side and clear the shared cookie — waited for (briefly) so the reload
      // below can't abort it.
      await Promise.race([
        api.auth.logout().catch(() => safeConsole.log('Logout API call completed (error ignored)')),
        new Promise((resolve) => setTimeout(resolve, LOGOUT_API_TIMEOUT_MS)),
      ]);
    } catch {
      safeConsole.error('Sign out error');
      clearLocalAuthState();
    } finally {
      // A full reload, like the patient portal: nothing of this account survives in memory, and the next
      // account starts from a fresh app.
      reloadToLogin();
    }
  }, [clearLocalAuthState]);

  // Universal inactivity logout (all roles), active only while logged in. See useIdleLogout.
  useIdleLogout(!!user, signOut);
  // The UI language follows the account across devices and is saved when changed (medopd-docs 106).
  useAccountLanguageSync(userData?.id ?? null, userData?.preferredLanguage, (lng) => api.auth.setMyLanguage(lng));

  const refreshAuth = async () => {
    try {
      const response = await api.auth.getCurrentUser();
      
      // Backend returns {data: {user, clinic}} which fetchApi wraps in {data: ...}
      const responseData = response.data?.data || response.data;
      
      if (responseData && responseData.user) {
        setUser({ id: responseData.user.id, email: responseData.user.name });
        setUserData(responseData.user);
        setClinic(responseData.clinic);
      } else {
        throw new Error('Invalid response from server: missing user data');
      }
    } catch (error) {
      safeConsole.error('Refresh auth error');
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    userData,
    clinic,
    loading,
    isSuperAdmin: userData?.role === 'super_admin',
    signIn,
    completeMfaLogin,
    completeEmailVerificationLogin,
    signInWithResponse,
    signOut,
    refreshAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** The auth context, or undefined outside an AuthProvider (widgets that also render in isolated tests or previews). */
export function useOptionalAuth(): AuthContextType | undefined {
  return useContext(AuthContext);
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
